import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { ChatEventUserMessageFragment } from '@spotify/confidence-graphql';

import { type ChatAttachment, attachmentKey } from '../../types';
import { AttachmentChip, ResourceAttachmentChip } from '../AttachmentChip';
import { PlainTextContent } from '../PlainTextContent';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--encore-spacing-tighter-2)',
    marginLeft: 'auto',
    width: 'fit-content',
    maxWidth: '80%',
    padding: 'var(--encore-spacing-tighter) var(--encore-spacing-base)',
    borderRadius: 'var(--encore-corner-radius-base)',
    backgroundColor: 'var(--background-elevated-highlight)',
  },
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--encore-spacing-tighter-2)',
    minWidth: 0,
  },
  attachments: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--encore-spacing-tighter-2)',
  },
  text: {
    wordBreak: 'break-word',
  },
});

interface UserMessageProps {
  content?: string | null;
  parts?: ChatEventUserMessageFragment['parts'];
  attachments?: ChatAttachment[];
}

export const UserMessage = ({
  content,
  parts,
  attachments = [],
}: UserMessageProps) => {
  const classes = useStyles();

  const attachmentParts = (parts ?? []).filter(p => p.resource);
  const textFromParts = (parts ?? [])
    .map(p => p.text)
    .filter((t): t is string => !!t)
    .join('\n\n');
  const bodyText = textFromParts || content || '';

  if (!bodyText && attachmentParts.length === 0 && attachments.length === 0) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <div className={classes.body}>
        {(attachments.length > 0 || attachmentParts.length > 0) && (
          <div className={classes.attachments}>
            {attachments.map(attachment => (
              <AttachmentChip
                key={attachmentKey(attachment)}
                attachment={attachment}
              />
            ))}
            {attachmentParts.map(part => (
              <ResourceAttachmentChip
                key={part.resource ?? ''}
                resourceName={part.resource ?? ''}
              />
            ))}
          </div>
        )}
        {bodyText && (
          <div className={classes.text}>
            <PlainTextContent>{bodyText}</PlainTextContent>
          </div>
        )}
      </div>
    </Box>
  );
};
